// Revoke a SKU grant.
// Endpoint: DELETE /api/admin/licensing/orgs/:org_id/skus/:sku_id
// Cascades: org_sku_features (FK ON DELETE CASCADE); active breaches
// auto-resolved as 'resolved_extended' (the measured resource is gone).
// Type-to-confirm if there are overrides or active breaches to lose.

import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { TOKENS } from "../design";
import { revokeSku } from "../../../../../services/licensingService";
import { SafeTextField } from "../../../../common/Greens";

const M_RevokeSku = ({ open, orgId, orgTitle, sku, activeBreachCount = 0, onClose, onConfirmed }) => {
  const overrideCount = (sku?.feature_overrides && sku.feature_overrides.length) || 0;
  const needsTypedConfirm = overrideCount > 0 || activeBreachCount > 0;
  const expectedToken = sku?.sku_id || "";

  const [typed, setTyped] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTyped("");
    setSaving(false);
    setErr(null);
  }, [open]);

  const canConfirm = useMemo(() => {
    if (!sku || saving) return false;
    if (!needsTypedConfirm) return true;
    return typed.trim() === expectedToken;
  }, [sku, saving, needsTypedConfirm, typed, expectedToken]);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSaving(true);
    setErr(null);
    try {
      await revokeSku(orgId, sku.sku_id);
      onConfirmed?.();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to revoke SKU");
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: `${TOKENS.radiusM}px`,
          boxShadow: TOKENS.shadowL,
          maxWidth: 480,
          width: "100%",
          background: TOKENS.bgSurface,
        },
      }}
    >
      <Box sx={{ p: "20px 22px 18px" }}>
        <Stack direction="row" spacing={1.75}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.dangerBg,
              color: TOKENS.dangerInk,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <DeleteOutlineIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: TOKENS.fg1, letterSpacing: "-0.01em" }}>
              Revoke SKU?
            </Typography>
            <Typography sx={{ fontSize: 13.5, color: TOKENS.fg2, mt: 0.75, lineHeight: 1.5 }}>
              Revoking <strong>{sku?.sku_name || sku?.sku_id}</strong> from{" "}
              <strong>{orgTitle}</strong>.
              {(overrideCount > 0 || activeBreachCount > 0) && (
                <>
                  {" "}This will cascade-delete <strong>{overrideCount}</strong> feature override
                  {overrideCount === 1 ? "" : "s"} and auto-resolve{" "}
                  <strong>{activeBreachCount}</strong> active breach
                  {activeBreachCount === 1 ? "" : "es"}.
                </>
              )}
            </Typography>
          </Box>
        </Stack>

        {needsTypedConfirm ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2, mb: 0.75 }}>
              Type{" "}
              <Box component="span" sx={{ fontFamily: "monospace", color: TOKENS.fg1, fontWeight: 700 }}>
                {expectedToken}
              </Box>{" "}
              to confirm.
            </Typography>
            <SafeTextField
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              size="small"
              fullWidth
              autoFocus
              placeholder={expectedToken}
              InputProps={{ sx: { fontSize: 13, fontFamily: "monospace" } }}
            />
          </Box>
        ) : null}

        {err ? (
          <Box
            sx={{
              mt: 2,
              p: 1.25,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.dangerBg,
              border: `1px solid ${TOKENS.dangerBorder}`,
              color: TOKENS.dangerInk,
              fontSize: 12.5,
            }}
          >
            {err}
          </Box>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2.5 }}>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: TOKENS.fg1,
              background: "transparent",
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              px: 2,
              "&:hover": { background: TOKENS.bgSunken },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#fff",
              background: TOKENS.danger,
              borderRadius: `${TOKENS.radiusXs}px`,
              px: 2,
              minWidth: 96,
              "&:hover": { background: TOKENS.dangerHover },
              "&.Mui-disabled": { color: "#fff", opacity: 0.55 },
            }}
          >
            {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Revoke"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default M_RevokeSku;
